import { BaseSanitizer } from './BaseSanitizer';
import * as ibantools from 'ibantools';
export class BankAccountSanitizer extends BaseSanitizer {
    constructor(context, enabled = true) {
        super(context, 'BANK_ACCOUNT', 'bankAccounts', enabled);
    }
    detectMatches(text) {
        const matches = [];
        const ibanMatches = this.detectIBANs(text);
        const swiftMatches = this.detectSWIFT(text);
        const abaMatches = this.detectABA(text);
        matches.push(...ibanMatches, ...swiftMatches, ...abaMatches);
        return matches;
    }
    detectIBANs(text) {
        const matches = [];
        // IBAN pattern: 2 letters, 2 digits, then up to 35 alphanumeric characters (optionally separated by spaces)
        // We use a greedy regex and then "shrink" from the right to find the longest valid IBAN.
        const ibanPattern = /\b[A-Z]{2}\d{2}(?:\s?[A-Z0-9]){11,35}\b/gi;
        let match;
        while ((match = ibanPattern.exec(text)) !== null) {
            const fullCandidate = match[0];
            const currentStartIndex = match.index;
            let tempCandidate = fullCandidate;
            let found = false;
            // Try to find the longest valid IBAN within this match by shrinking from the right
            while (tempCandidate.length >= 15) { // Minimum IBAN length is usually 15
                const cleanIBAN = tempCandidate.replace(/\s/g, '').toUpperCase();
                if (ibantools.isValidIBAN(cleanIBAN)) {
                    matches.push({
                        originalText: tempCandidate,
                        tag: '',
                        startIndex: currentStartIndex,
                        endIndex: currentStartIndex + tempCandidate.length
                    });
                    // Advance the regex lastIndex to the end of the found IBAN
                    ibanPattern.lastIndex = currentStartIndex + tempCandidate.length;
                    found = true;
                    break;
                }
                // Remove one character from the end and trim any resulting trailing spaces
                tempCandidate = tempCandidate.substring(0, tempCandidate.length - 1).trimEnd();
            }
            // If no valid IBAN was found in the greedy match, we continue searching after the start of this match
            if (!found) {
                ibanPattern.lastIndex = currentStartIndex + 1;
            }
        }
        return matches;
    }
    detectSWIFT(text) {
        const matches = [];
        const swiftPattern = /\b[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?\b/gi;
        let match;
        while ((match = swiftPattern.exec(text)) !== null) {
            if (this.looksLikeSWIFTContext(text, match.index) && !this.looksLikeCommonWord(match[0])) {
                matches.push({
                    originalText: match[0],
                    tag: '',
                    startIndex: match.index,
                    endIndex: match.index + match[0].length
                });
            }
        }
        return matches;
    }
    detectABA(text) {
        const matches = [];
        const abaPattern = /\b\d{9}\b/g;
        let match;
        while ((match = abaPattern.exec(text)) !== null) {
            if (this.isValidABAChecksum(match[0]) && this.looksLikeABAContext(text, match.index)) {
                matches.push({
                    originalText: match[0],
                    tag: '',
                    startIndex: match.index,
                    endIndex: match.index + match[0].length
                });
            }
        }
        return matches;
    }
    isValidABAChecksum(routingNumber) {
        const digits = routingNumber.split('').map(Number);
        const sum = 3 * (digits[0] + digits[3] + digits[6]) +
            7 * (digits[1] + digits[4] + digits[7]) +
            (digits[2] + digits[5] + digits[8]);
        return sum % 10 === 0;
    }
    looksLikeSWIFTContext(text, index) {
        const before = text.substring(Math.max(0, index - 20), index).toLowerCase();
        const after = text.substring(index, Math.min(text.length, index + 10)).toLowerCase();
        const keywords = ['swift', 'bic', 'code', 'wire', 'transfer', 'international', 'iban', 'bank'];
        return keywords.some(keyword => before.includes(keyword) || after.includes(keyword));
    }
    looksLikeABAContext(text, index) {
        const before = text.substring(Math.max(0, index - 20), index).toLowerCase();
        const after = text.substring(index, Math.min(text.length, index + 10)).toLowerCase();
        const keywords = ['routing', 'aba', 'bank', 'account', 'wire', 'transfer', 'check'];
        return keywords.some(keyword => before.includes(keyword) || after.includes(keyword));
    }
    looksLikeCommonWord(text) {
        const commonWords = ['transfer', 'deposit', 'payment', 'account', 'banking'];
        return commonWords.includes(text.toLowerCase());
    }
}
